/**
 * Height/weight size recommender.
 *
 * Reads bands from theme settings, works out a size from weight and a size from
 * height independently, and recommends the LARGER of the two. Erring roomy is
 * deliberate for an oversized-cut brand: too tight is the return that cannot be
 * salvaged, too big usually still gets worn. Both readings are always shown, so
 * a shopper whose height and weight disagree can see why and overrule it.
 *
 * Nothing is computed from a size the product does not stock — the recommended
 * size is mapped onto the product's own option values, and when it is out of
 * stock the nearest available size is offered instead of a dead end.
 */

const STORAGE_KEY = 'fandom-size-finder';
const CANONICAL = ['M', 'L', 'XL', '2XL'];

/** Half the thumb width in the stylesheet. The track visuals are inset by the
 *  same amount, so this is the one number the two files have to agree on. */
const THUMB_INSET = 16;

/**
 * @param {string} raw - The `Size|min-max kg|min-max cm` lines from settings
 * @returns {Array<{size: string, weight: [number, number], height: [number, number]}>}
 */
function parseBands(raw) {
  return String(raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [size, weight, height] = line.split('|').map((part) => (part || '').trim());
      const range = (text) => {
        const [min, max] = String(text).split('-').map((n) => parseFloat(n));
        return [min, max];
      };
      return { size: (size || '').toUpperCase(), weight: range(weight), height: range(height) };
    })
    .filter((band) => band.size && band.weight.every(Number.isFinite) && band.height.every(Number.isFinite));
}

/**
 * Which band a measurement falls in.
 *
 * A value sitting exactly on a boundary goes to the LARGER size (65kg is L, not
 * M), matching the recommendation rule. Anything past either end clamps to the
 * end band and is reported as clamped so the shopper is told rather than being
 * silently handed the extreme.
 *
 * Only the upper bound decides the band, which is what closes any gap between
 * one row's top and the next row's bottom — 168cm lands in L even when the
 * authored table starts L at 170.
 *
 * @param {number} value
 * @param {Array<object>} bands
 * @param {'weight'|'height'} key
 * @returns {{index: number, clamped: 'low'|'high'|null} | null}
 */
function bandIndex(value, bands, key) {
  if (!Number.isFinite(value) || !bands.length) return null;

  if (value <= bands[0][key][0]) return { index: 0, clamped: 'low' };

  for (let i = 0; i < bands.length; i++) {
    if (value < bands[i][key][1]) return { index: i, clamped: null };
  }

  return { index: bands.length - 1, clamped: 'high' };
}

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class FandomSizeFinder extends HTMLElement {
  /** Until the shopper moves something, the sliders are showing defaults, not them. */
  #touched = false;
  /** Signature of what the answer block currently shows, so it is only rebuilt when it would differ. */
  #rendered = null;
  /** Pending rAF handle — a drag emits far more moves than there are frames. */
  #frame = 0;
  /** @type {HTMLElement[]} */
  #steps = [];

  connectedCallback() {
    this.dialog = this.querySelector('[data-size-finder-dialog]');
    this.heightSlider = this.querySelector('[data-size-finder-height]');
    this.weightSlider = this.querySelector('[data-size-finder-weight]');
    this.heightNumber = this.querySelector('[data-size-finder-height-number]');
    this.weightNumber = this.querySelector('[data-size-finder-weight-number]');
    this.result = this.querySelector('[data-size-finder-result]');
    this.scale = this.querySelector('[data-size-finder-scale]');
    this.reason = this.querySelector('[data-size-finder-reason]');
    this.chooseButton = this.querySelector('[data-size-finder-choose]');
    this.chart = this.querySelector('.size-finder__chart');

    if (!this.dialog || !this.heightSlider || !this.weightSlider || !this.result) return;

    this.bands = parseBands(this.dataset.bands);
    this.sizes = this.#json('sizes', []);
    this.measurements = this.#json('measurements', {});

    if (!this.bands.length || !this.sizes.length) return;

    // This element renders inside the variant picker, which treats ANY change
    // event reaching it as a variant selection and refetches the whole product
    // section (variant-picker.js:48). A range input fires `change` on release,
    // so letting ours escape meant the section was re-rendered the moment a
    // slider was let go — the dialog stayed open over markup that had just been
    // replaced, blank and dead. Our controls keep their events to themselves;
    // the radio that "Choose" clicks lives outside this element, so the one
    // change that SHOULD reach the picker still does.
    for (const type of ['input', 'change']) {
      this.addEventListener(type, (event) => event.stopPropagation());
    }

    // Enter in a number field inside a product form is an implicit submit —
    // that is the add-to-cart button.
    for (const input of [this.heightNumber, this.weightNumber]) {
      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
    }

    this.querySelector('[data-size-finder-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-size-finder-close]')?.addEventListener('click', () => this.dialog.close());
    this.chooseButton?.addEventListener('click', () => this.#choose());

    this.#pair(this.heightSlider, this.heightNumber);
    this.#pair(this.weightSlider, this.weightNumber);
    this.#drag(this.heightSlider);
    this.#drag(this.weightSlider);

    // The scale never changes shape — one step per size this product sells —
    // so it is built once and only its selected class is touched afterwards.
    this.#buildScale();

    this.#restore();
    this.#syncNumbers();
    this.update();
  }

  /** @param {string} key @param {any} fallback */
  #json(key, fallback) {
    try {
      return JSON.parse(this.dataset[key] || '');
    } catch {
      return fallback;
    }
  }

  /**
   * @param {HTMLInputElement} slider
   * @param {HTMLInputElement | null} number
   */
  #pair(slider, number) {
    slider.addEventListener('input', () => {
      this.#touched = true;
      if (number) number.value = slider.value;
      this.update();
    });

    number?.addEventListener('input', () => {
      this.#touched = true;
      // Only follow the number once it is a value the slider can hold, so a
      // half-typed "1" does not yank the handle to the minimum.
      const value = parseFloat(number.value);
      if (Number.isFinite(value) && value >= Number(slider.min) && value <= Number(slider.max)) {
        slider.value = String(value);
      }
      this.update();
    });
  }

  /**
   * Drive the drag ourselves.
   *
   * A range input only moves when the pointer lands on its thumb, and the thumb
   * here is a 4px bar — so the tape could be tapped but not swiped. Widening the
   * hit box helped and was still at the mercy of how each browser decides a drag
   * has begun. Pointer events remove the question: press anywhere on the tape and
   * the bar follows the finger until it lifts, on every engine, with the pointer
   * captured so it keeps tracking outside the element. The input keeps its value,
   * its keyboard behaviour and its accessibility tree.
   *
   * @param {HTMLInputElement} slider
   */
  #drag(slider) {
    let dragging = false;

    slider.addEventListener('pointerdown', (event) => {
      if (event.button > 0) return;
      dragging = true;
      slider.setPointerCapture?.(event.pointerId);
      slider.focus?.({ preventScroll: true });
      this.#seek(slider, event.clientX);
      // Suppress the native thumb drag so the two cannot fight over the value.
      event.preventDefault();
    });

    slider.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      this.#seek(slider, event.clientX);
      event.preventDefault();
    });

    const release = (event) => {
      if (!dragging) return;
      dragging = false;
      try {
        slider.releasePointerCapture?.(event.pointerId);
      } catch {
        // Already released — the capture is gone either way.
      }
    };

    slider.addEventListener('pointerup', release);
    slider.addEventListener('pointercancel', release);
  }

  /**
   * Position on the tape -> value, snapped to the input's own step.
   * @param {HTMLInputElement} slider
   * @param {number} clientX
   */
  #seek(slider, clientX) {
    const rect = slider.getBoundingClientRect();
    const usable = rect.width - THUMB_INSET * 2;
    if (usable <= 0) return;

    const min = Number(slider.min);
    const max = Number(slider.max);
    const step = Number(slider.step) || 1;

    const ratio = Math.min(1, Math.max(0, (clientX - rect.left - THUMB_INSET) / usable));
    const value = Math.round((min + ratio * (max - min)) / step) * step;
    if (String(value) === slider.value) return;

    slider.value = String(value);
    this.#touched = true;

    const number = slider === this.heightSlider ? this.heightNumber : this.weightNumber;
    if (number) number.value = slider.value;

    this.#schedule();
  }

  /** A drag fires far more moves than there are frames to paint them. */
  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.update();
    });
  }

  #syncNumbers() {
    if (this.heightNumber) this.heightNumber.value = this.heightSlider.value;
    if (this.weightNumber) this.weightNumber.value = this.weightSlider.value;
  }

  /** @param {HTMLInputElement} slider */
  #fill(slider) {
    const min = Number(slider.min);
    const max = Number(slider.max);
    const value = Number(slider.value);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return;

    const percent = ((value - min) / (max - min)) * 100;
    slider.style.setProperty('--sf-fill', `${Math.min(100, Math.max(0, percent))}%`);
  }

  /** Pre-fill from the last product they measured themselves on. */
  #restore() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (saved.height) {
        this.heightSlider.value = saved.height;
        this.#touched = true;
      }
      if (saved.weight) {
        this.weightSlider.value = saved.weight;
        this.#touched = true;
      }
    } catch {
      // A blocked or corrupt localStorage is not a reason to break the tool.
    }
  }

  #save(height, weight) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ height, weight }));
    } catch {
      // Private mode. The recommendation still works, it just will not persist.
    }
  }

  /** @param {string} canon */
  #optionFor(canon) {
    return this.sizes.find((size) => size.canon === canon) ?? null;
  }

  /**
   * Nearest size this product actually has in stock, preferring the larger one
   * when two are equally close — same reasoning as the recommendation itself.
   * @param {number} index - Index into CANONICAL of the wanted size
   */
  #nearestAvailable(index) {
    const inStock = this.sizes.filter((size) => size.available);
    if (!inStock.length) return null;

    return inStock
      .map((size) => ({ size, distance: Math.abs(CANONICAL.indexOf(size.canon) - index) }))
      .sort((a, b) => {
        if (a.distance !== b.distance) return a.distance - b.distance;
        return CANONICAL.indexOf(b.size.canon) - CANONICAL.indexOf(a.size.canon);
      })[0].size;
  }

  open() {
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.update();
  }

  update() {
    this.#fill(this.heightSlider);
    this.#fill(this.weightSlider);

    const height = parseFloat(this.heightSlider.value);
    const weight = parseFloat(this.weightSlider.value);

    const byHeight = bandIndex(height, this.bands, 'height');
    const byWeight = bandIndex(weight, this.bands, 'weight');
    if (!byHeight || !byWeight) return;

    if (this.#touched) this.#save(this.heightSlider.value, this.weightSlider.value);
    this.toggleAttribute('data-size-finder-untouched', !this.#touched);

    // The larger of the two readings wins.
    const canon = this.bands[Math.max(byHeight.index, byWeight.index)].size;

    const wanted = this.#optionFor(canon);
    const inStock = Boolean(wanted && wanted.available);
    const fallback = inStock ? null : this.#nearestAvailable(CANONICAL.indexOf(canon));
    const offered = inStock ? wanted : fallback;

    // The reason line changes with every centimetre, so it is written as text
    // on an element that already exists rather than rebuilt as markup.
    this.#writeReason(byHeight, byWeight, height, weight, canon);

    // Everything else only changes when the ANSWER changes, which across a whole
    // drag happens three or four times at most.
    const signature = [canon, offered?.value ?? '', inStock, this.#touched, byHeight.clamped, byWeight.clamped].join('|');
    if (signature === this.#rendered) return;
    this.#rendered = signature;

    this.#renderVerdict(canon, wanted, inStock, fallback);
    this.#markScale(offered ? offered.canon : canon);
    this.#renderChoose(offered);
    this.#highlight(canon);
  }

  /**
   * @param {string} canon
   * @param {object | null} wanted
   * @param {boolean} inStock
   * @param {object | null} fallback
   */
  #renderVerdict(canon, wanted, inStock, fallback) {
    const label = wanted ? wanted.value : canon;
    const shown = inStock ? label : fallback ? fallback.value : label;
    const dimensions = this.measurements?.[inStock || !fallback ? canon : fallback.canon] ?? [];

    this.result.innerHTML =
      `<p class="size-finder__verdict-label">${this.#touched ? 'Your size' : 'Drag to your measurements'}</p>` +
      `<p class="size-finder__verdict">${escapeHtml(shown)}</p>` +
      (dimensions.length
        ? `<p class="size-finder__dimensions">${dimensions.map((d) => escapeHtml(d)).join(' &middot; ')}</p>`
        : '') +
      (!inStock && fallback
        ? `<p class="size-finder__swap">${escapeHtml(label)} is sold out — ${escapeHtml(fallback.value)} is the closest we have.</p>`
        : '') +
      (!inStock && !fallback ? `<p class="size-finder__swap">Every size of this one is sold out.</p>` : '');
  }

  /** One step per size the product sells. Built once; only the class moves. */
  #buildScale() {
    if (!this.scale) return;

    this.#steps = this.sizes.map((size) => {
      const step = document.createElement('span');
      step.className = `size-finder__step${size.available ? '' : ' is-out'}`;
      step.textContent = size.value;
      step.dataset.canon = size.canon;
      return step;
    });

    this.scale.replaceChildren(...this.#steps);
  }

  /** @param {string} current */
  #markScale(current) {
    for (const step of this.#steps) {
      step.classList.toggle('is-current', step.dataset.canon === current);
    }
  }

  /**
   * @param {{index: number, clamped: string|null}} byHeight
   * @param {{index: number, clamped: string|null}} byWeight
   */
  #writeReason(byHeight, byWeight, height, weight, canon) {
    if (!this.reason) return;

    let text = `${height} cm suggests ${this.bands[byHeight.index].size} · ${weight} kg suggests ${this.bands[byWeight.index].size}`;

    if (byHeight.clamped === 'low' || byWeight.clamped === 'low') {
      text += ` · ${canon} is the smallest we make`;
    } else if (byHeight.clamped === 'high' || byWeight.clamped === 'high') {
      text += ` · ${canon} is the largest we make`;
    }

    if (this.reason.textContent !== text) this.reason.textContent = text;
  }

  /** @param {object | null} offered */
  #renderChoose(offered) {
    if (!this.chooseButton) return;

    if (!offered) {
      this.chooseButton.hidden = true;
      return;
    }

    this.chooseButton.hidden = false;
    this.chooseButton.textContent = `Choose ${offered.value}`;
    this.chooseButton.dataset.sizeValue = offered.value;
  }

  /** @param {string | null} canon */
  #highlight(canon) {
    for (const row of this.chart?.querySelectorAll('[data-band-row]') ?? []) {
      row.toggleAttribute('data-band-current', row.getAttribute('data-band-row') === canon);
    }
  }

  #choose() {
    const value = this.chooseButton?.dataset.sizeValue;
    if (!value) return;

    const scope = this.closest('form') ?? this.parentElement ?? document;
    const radios = [
      ...scope.querySelectorAll('input[type="radio"][data-option-name]'),
      ...document.querySelectorAll('input[type="radio"][data-option-name]'),
    ];

    const target = radios.find(
      (radio) =>
        (radio.dataset.optionName || '').trim().toLowerCase() === 'size' &&
        radio.value.trim().toLowerCase() === value.trim().toLowerCase()
    );

    // Click rather than set `checked`: the variant picker listens for the
    // change a real interaction fires.
    if (target) target.click();
    this.dialog.close();
  }
}

if (!customElements.get('fandom-size-finder')) {
  customElements.define('fandom-size-finder', FandomSizeFinder);
}
